/**
 * JSON Content Adapter
 *
 * Provides bidirectional conversion between JSONContent format (used for
 * API responses and storage) and ProseMirror Node objects (used by the editor).
 *
 * JSONContent format:
 * {
 *   type: "doc",
 *   content: [
 *     { type: "paragraph", content: [{ type: "text", text: "Hello" }] }
 *   ]
 * }
 *
 * This adapter ensures documents stored in the database can be loaded
 * into the editor and saved back in a compatible format.
 */

import type { Node as ProseMirrorNode, Mark } from "@tiptap/pm/model";
import { schema } from "../schema";

/**
 * JSONContent type matching the format used for storage.
 * This is the same format as TipTap's JSONContent.
 */
export interface JSONContent {
  type: string;
  attrs?: Record<string, unknown>;
  content?: JSONContent[];
  marks?: JSONMark[];
  text?: string;
}

export interface JSONMark {
  type: string;
  attrs?: Record<string, unknown>;
}

/**
 * Convert JSONContent to a ProseMirror Node.
 *
 * @param json - The JSON content to convert
 * @returns A ProseMirror Node, or null if conversion fails
 */
export function jsonContentToProseMirror(
  json: JSONContent | null | undefined
): ProseMirrorNode | null {
  if (!json) {
    return null;
  }

  try {
    // Handle root doc node
    if (json.type === "doc") {
      const content = (json.content ?? [])
        .map((child) => jsonNodeToNode(child))
        .filter((node): node is ProseMirrorNode => node !== null);

      // Ensure at least one paragraph if content is empty
      if (content.length === 0) {
        content.push(schema.node("paragraph"));
      }

      return schema.node("doc", json.attrs ?? null, content);
    }

    // If passed a non-doc node, wrap it in a doc
    const node = jsonNodeToNode(json);
    if (node) {
      return schema.node("doc", null, [node]);
    }

    return null;
  } catch (error) {
    console.error("Failed to convert JSONContent to ProseMirror:", error);
    return null;
  }
}

/**
 * Convert a ProseMirror Node to JSONContent.
 *
 * @param node - The ProseMirror node to convert
 * @returns JSONContent representation
 */
export function proseMirrorToJsonContent(
  node: ProseMirrorNode
): JSONContent {
  return nodeToJsonContent(node);
}

/**
 * Convert a single JSON node to ProseMirror node.
 */
function jsonNodeToNode(json: JSONContent): ProseMirrorNode | null {
  const nodeType = schema.nodes[json.type];

  if (!nodeType) {
    // Unknown node type - try to handle gracefully
    console.warn(`Unknown node type: ${json.type}, skipping`);
    return null;
  }

  // Handle text nodes
  if (json.type === "text") {
    const text = json.text ?? "";
    if (!text) {
      return null;
    }

    const marks = (json.marks ?? [])
      .map((mark) => jsonMarkToMark(mark))
      .filter((m): m is Mark => m !== null);

    return schema.text(text, marks);
  }

  // Handle hardBreak nodes
  if (json.type === "hardBreak") {
    return schema.node("hardBreak");
  }

  // Handle block nodes with content
  if (nodeType.isBlock || nodeType.name === "paragraph") {
    const content = (json.content ?? [])
      .map((child) => jsonNodeToNode(child))
      .filter((node): node is ProseMirrorNode => node !== null);

    // Get attrs, ensuring blockKey is preserved
    const attrs = { ...json.attrs };

    return schema.node(json.type, attrs, content);
  }

  // Fallback for other node types
  const content = (json.content ?? [])
    .map((child) => jsonNodeToNode(child))
    .filter((node): node is ProseMirrorNode => node !== null);

  return schema.node(json.type, json.attrs ?? null, content);
}

/**
 * Convert a JSON mark to ProseMirror mark.
 */
function jsonMarkToMark(json: JSONMark): Mark | null {
  const markType = schema.marks[json.type];

  if (!markType) {
    console.warn(`Unknown mark type: ${json.type}, skipping`);
    return null;
  }

  return markType.create(json.attrs ?? null);
}

/**
 * Convert a ProseMirror node to JSON.
 */
function nodeToJsonContent(node: ProseMirrorNode): JSONContent {
  // Handle text nodes
  if (node.isText) {
    const result: JSONContent = {
      type: "text",
      text: node.text ?? "",
    };

    if (node.marks.length > 0) {
      result.marks = node.marks.map((mark) => markToJson(mark));
    }

    return result;
  }

  // Handle other nodes
  const result: JSONContent = {
    type: node.type.name,
  };

  // Add attrs if present and not default
  const attrs = node.attrs;
  if (attrs && Object.keys(attrs).length > 0) {
    const nonDefaultAttrs: Record<string, unknown> = {};
    const spec = node.type.spec;
    const attrSpecs = spec.attrs as Record<string, { default?: unknown }> | undefined;

    for (const [key, value] of Object.entries(attrs)) {
      // Only include non-default values
      const defaultValue = attrSpecs?.[key]?.default;
      if (value !== defaultValue) {
        nonDefaultAttrs[key] = value;
      }
    }
    if (Object.keys(nonDefaultAttrs).length > 0) {
      result.attrs = nonDefaultAttrs;
    }
  }

  // Add content if present
  if (node.content.size > 0) {
    const content: JSONContent[] = [];
    node.content.forEach((child) => {
      content.push(nodeToJsonContent(child));
    });
    result.content = content;
  }

  return result;
}

/**
 * Convert a ProseMirror mark to JSON.
 * Filters out attributes that match their default values.
 */
function markToJson(mark: Mark): JSONMark {
  const result: JSONMark = {
    type: mark.type.name,
  };

  // Add attrs if present, filtering out defaults (same as nodeToJsonContent)
  const attrs = mark.attrs;
  if (attrs && Object.keys(attrs).length > 0) {
    const nonDefaultAttrs: Record<string, unknown> = {};
    const spec = mark.type.spec;
    const attrSpecs = spec.attrs as
      | Record<string, { default?: unknown }>
      | undefined;

    for (const [key, value] of Object.entries(attrs)) {
      const defaultValue = attrSpecs?.[key]?.default;
      if (value !== defaultValue) {
        nonDefaultAttrs[key] = value;
      }
    }

    if (Object.keys(nonDefaultAttrs).length > 0) {
      result.attrs = nonDefaultAttrs;
    }
  }

  return result;
}

/**
 * Create an empty document in JSONContent format.
 */
export function createEmptyJsonContent(): JSONContent {
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [],
      },
    ],
  };
}

/**
 * Validate that JSONContent is well-formed.
 * Does basic structural validation without fully parsing.
 */
export function isValidJsonContent(json: unknown): json is JSONContent {
  if (!json || typeof json !== "object") {
    return false;
  }

  const content = json as Record<string, unknown>;

  if (typeof content.type !== "string") {
    return false;
  }

  if (content.content !== undefined && !Array.isArray(content.content)) {
    return false;
  }

  if (content.text !== undefined && typeof content.text !== "string") {
    return false;
  }

  return true;
}
